<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { resetPassword } from '@/api/auth'
import { useAuthContext, AUTH_CONTEXT_QUERY_PARAM } from '@/composables/useAuthContext'
import AppFooter from '@/components/layout/AppFooter.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import type { RouteLocationRaw } from 'vue-router'

const { t } = useI18n()
const route = useRoute()
const { authContext, loginRoute } = useAuthContext()

// The reset link itself is emailed and, as generated by the backend today,
// carries only `token` and `email` — no context. If a `context` param is
// ever present (e.g. reached via an in-app link) it is preserved when
// bouncing back to the forgot-password page after an invalid/expired link.
const forgotPasswordRoute = computed<RouteLocationRaw>(() => ({
  name: 'organizer.forgot-password',
  query: { [AUTH_CONTEXT_QUERY_PARAM]: authContext.value },
}))

const token = ref('')
const email = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const loading = ref(false)
const success = ref(false)
const invalidLink = ref(false)
const errors = ref<Record<string, string[]>>({})

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

onMounted(() => {
  const tokenParam = route.query.token
  const emailParam = route.query.email
  if (!tokenParam || !emailParam) {
    invalidLink.value = true
    return
  }
  token.value = String(tokenParam)
  email.value = String(emailParam)
})

async function handleSubmit(): Promise<void> {
  errors.value = {}
  loading.value = true
  try {
    await resetPassword({
      token: token.value,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    })
    success.value = true
  } catch (e: unknown) {
    if (isAxiosError(e) && e.response.status === 422 && e.response.data.errors) {
      errors.value = e.response.data.errors
    } else {
      errors.value = { _general: [t('common.error_generic')] }
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-slate-900 flex flex-col">
    <div class="flex-1 flex items-center justify-center px-4 py-12">
    <div class="w-full max-w-md">
      <!-- Wordmark -->
      <div class="text-center mb-8">
        <a href="/" class="inline-flex items-center gap-2 group">
          <span class="text-2xl font-black text-white tracking-tight">Startline</span>
          <span class="w-2 h-2 rounded-full bg-emerald-400 mb-0.5" />
        </a>
      </div>

      <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
        <!-- Invalid link -->
        <div v-if="invalidLink" class="text-center">
          <p class="text-red-400 text-sm mb-4">{{ t('auth.reset_password.invalid_link') }}</p>
          <RouterLink :to="forgotPasswordRoute" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
            {{ t('auth.forgot_password.heading') }}
          </RouterLink>
        </div>

        <!-- Success state -->
        <div v-else-if="success" class="text-center">
          <div class="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-4 text-sm text-emerald-400 mb-4">
            {{ t('auth.reset_password.success') }}
          </div>
          <RouterLink :to="loginRoute" class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
            {{ t('auth.reset_password.go_to_login') }}
          </RouterLink>
        </div>

        <!-- Form -->
        <form v-else novalidate @submit.prevent="handleSubmit" class="space-y-5">
          <h1 class="text-2xl font-bold text-white mb-6">{{ t('auth.reset_password.heading') }}</h1>

          <div v-if="errors._general" class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400">
            {{ errors._general[0] }}
          </div>

          <!-- Email (read-only) -->
          <div>
            <label for="reset-email" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('auth.login.email_label') }}
            </label>
            <input
              id="reset-email"
              :value="email"
              type="email"
              readonly
              class="w-full rounded-lg bg-slate-700 border border-slate-600 px-4 py-2.5 text-slate-400 text-sm cursor-not-allowed"
            />
            <p v-if="errors.email" class="mt-1 text-xs text-red-400">{{ errors.email[0] }}</p>
          </div>

          <div>
            <label for="reset-password" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('auth.reset_password.password_label') }}
            </label>
            <input
              id="reset-password"
              v-model="password"
              type="password"
              autocomplete="new-password"
              :placeholder="t('auth.reset_password.password_placeholder')"
              class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.password ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            />
            <p v-if="errors.password" class="mt-1 text-xs text-red-400">{{ errors.password[0] }}</p>
          </div>

          <div>
            <label for="reset-password-confirm" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('auth.reset_password.confirmation_label') }}
            </label>
            <input
              id="reset-password-confirm"
              v-model="passwordConfirmation"
              type="password"
              autocomplete="new-password"
              class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
              :class="errors.password_confirmation ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
            />
            <p v-if="errors.password_confirmation" class="mt-1 text-xs text-red-400">{{ errors.password_confirmation[0] }}</p>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
          >
            <AppSpinner v-if="loading" size="button" />
            {{ loading ? t('common.loading') : t('auth.reset_password.submit') }}
          </button>

          <p class="text-sm text-center">
            <RouterLink :to="loginRoute" class="text-slate-400 hover:text-slate-300 transition-colors">
              ← {{ t('auth.reset_password.go_to_login') }}
            </RouterLink>
          </p>
        </form>
      </div>
    </div>
    </div>
    <AppFooter />
  </div>
</template>
